import React, { createContext, useContext, useEffect, useMemo } from "react";

import {
  Branding,
  ExternalLink,
  Maybe,
  ProfileSettings,
  WelcomeMessage,
} from "../generated/lmsTypes";
import { useLmsQuery } from "../hooks/lms";
import { useGraphQLContext } from "./GraphQLProvider";
import {
  PORTAL_CUSTOM_VIEWER_FIELD_QUERY,
  PORTAL_FOR_HOST_QUERY,
} from "../queries/portal";
import {
  CustomViewerFields,
  CustomViewerFieldsResponse,
  PortalForHostResponse,
} from "../types/Portal";

type PortalContextType = {
  externalLinks: Array<Maybe<ExternalLink>> | [];
  profileSettings?: ProfileSettings;
  isPortalLoading: boolean;
  isCustomViewerFieldsLoading: boolean;
  customViewerFields: Array<CustomViewerFields>;
  termsAndConditions: string;
  isApprovalPortal: boolean;
  isBookingPortal: boolean;
  isSelfRegistrationPortal: boolean;
  host: string;
  brandName: string;
  brandId: string;
  welcomeMessage?: WelcomeMessage;
  isSingleSignOnOnly: boolean;
  emptyPortalRedirectUrl?: Maybe<string>;
  branding?: Branding;
  showPrivateTraining: boolean;
  customFieldDependenciesEnabled: boolean;
  selfHostedVideoEnabled: boolean;
};

const PortalContext = createContext<PortalContextType | undefined>(undefined);

export const usePortal = () => {
  const context = useContext(PortalContext);
  if (context === undefined) {
    throw new Error("PortalProvider not found");
  }
  return context;
};

export const getHost = (): string => {
  const pageUrl = new URL(window.location.href);
  return `${pageUrl.protocol}//${pageUrl.hostname}`;
};

export const PortalProvider: React.FunctionComponent<
  React.PropsWithChildren<unknown>
> = ({ children }) => {
  const host = useMemo<string>(() => getHost(), []);
  const { bookingWebLinkClient, selfRegistrationWebLinkClient } =
    useGraphQLContext();

  const { data: portalForHostResponse, loading: isPortalLoading } =
    useLmsQuery<PortalForHostResponse>(PORTAL_FOR_HOST_QUERY, {
      variables: { host },
    });

  const {
    data: customViewerFieldsResponse,
    loading: isCustomViewerFieldsLoading,
  } = useLmsQuery<CustomViewerFieldsResponse>(
    PORTAL_CUSTOM_VIEWER_FIELD_QUERY,
    {
      variables: { host },
    },
  );

  const externalLinks =
    portalForHostResponse?.portalForHost?.externalLinks || [];

  const brand = portalForHostResponse?.portalForHost?.brand;
  const brandName = brand?.name || "";
  const brandId = brand?.id || "";
  const welcomeMessage = brand?.welcomeMessage;
  useEffect(() => {
    document.title = brandName;
  }, [brandName]);

  const customViewerFields =
    customViewerFieldsResponse?.portalCustomFieldTemplates || [];

  const termsAndConditions =
    portalForHostResponse?.portalForHost?.termsAndConditions || "";

  const isApprovalPortal =
    portalForHostResponse?.portalForHost?.isApprovalPortal ?? false;

  const isBookingPortal =
    !!portalForHostResponse?.portalForHost?.isBookingPortal &&
    !!bookingWebLinkClient;

  const isSelfRegistrationPortal =
    !!portalForHostResponse?.portalForHost?.isSelfRegistrationPortal &&
    !!selfRegistrationWebLinkClient;

  const profileSettings = portalForHostResponse?.portalForHost?.profileSettings;
  const isSingleSignOnOnly =
    portalForHostResponse?.portalForHost?.brand?.singleSignOnOnly || false;
  const showPrivateTraining =
    portalForHostResponse?.portalForHost?.linkedWebLinkPortals?.[0]
      ?.showPrivateTraining ?? false;

  // Off while the portal query is in flight, so a field renders unnarrowed
  // rather than narrowed by rules resolved against a parent value nobody has
  // loaded yet.
  const customFieldDependenciesEnabled =
    portalForHostResponse?.portalForHost?.customFieldDependenciesEnabled ??
    false;

  const emptyPortalRedirectUrl = isPortalLoading
    ? null
    : portalForHostResponse?.portalForHost?.emptyPortalRedirectUrl || "";

  return (
    <PortalContext.Provider
      value={{
        externalLinks,
        isPortalLoading,
        isCustomViewerFieldsLoading,
        customViewerFields,
        termsAndConditions,
        isApprovalPortal,
        isBookingPortal,
        isSelfRegistrationPortal,
        host,
        brandName,
        brandId,
        welcomeMessage,
        isSingleSignOnOnly,
        profileSettings,
        emptyPortalRedirectUrl,
        branding: portalForHostResponse?.portalForHost?.brand.branding,
        showPrivateTraining,
        customFieldDependenciesEnabled,
        selfHostedVideoEnabled:
          portalForHostResponse?.portalForHost?.selfHostedVideoEnabled ?? false,
      }}
    >
      {children}
    </PortalContext.Provider>
  );
};
